import React from "react";
import { ImagePlusIcon } from "lucide-react";

import { cn } from "@javascript/components/utils";
import { Button } from "@javascript/components/ui/button";
import { FieldError } from "@javascript/components/ui/field";

const NOT_AN_IMAGE = "Only image files are accepted.";
const MORE_THAN_ONE_FILE = "Drop one image at a time.";

function rejectionFor(files: FileList): string | null {
  if (files.length > 1) return MORE_THAN_ONE_FILE;
  if (!files[0].type.startsWith("image/")) return NOT_AN_IMAGE;
  return null;
}

export function InvitationFileInput({
  id,
  name,
  accept,
  url,
  required = false,
  prompt = "Drop your cover photo here, or browse",
  previewAlt = "Selected cover photo",
}: {
  id: string;
  name: string;
  accept: string;
  url?: string | null;
  required?: boolean;
  prompt?: string;
  previewAlt?: string;
}) {
  const [file, setFile] = React.useState<File | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [dragActive, setDragActive] = React.useState(false);
  const [chosenPreviewUrl, setChosenPreviewUrl] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (!file) {
      setChosenPreviewUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setChosenPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  const previewUrl = chosenPreviewUrl ?? url ?? null;

  function chooseFiles(files: FileList | null) {
    if (!files?.length) return false;
    const rejection = rejectionFor(files);
    if (rejection) {
      setError(rejection);
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      return false;
    }
    setError(null);
    setFile(files[0]);
    return true;
  }

  return (
    <div
      data-slot="file-drop-area"
      onClick={(event) => {
        if (event.target !== inputRef.current) inputRef.current?.click();
      }}
      onDragOver={(event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
        setDragActive(true);
      }}
      onDragEnter={(event) => {
        event.preventDefault();
        setDragActive(true);
      }}
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragActive(false);
      }}
      onDrop={(event) => {
        event.preventDefault();
        setDragActive(false);
        const dropped = event.dataTransfer.files;
        if (chooseFiles(dropped) && inputRef.current) inputRef.current.files = dropped;
      }}
      className={cn(
        "flex cursor-pointer flex-col items-center gap-3 rounded-md border border-dashed border-input bg-muted/50 px-6 py-8 text-center transition-colors",
        "has-[:focus-visible]:border-ring has-[:focus-visible]:ring-[3px] has-[:focus-visible]:ring-ring/50",
        dragActive && "border-primary bg-accent",
        error && "border-destructive",
      )}
    >
      <input
        ref={inputRef}
        id={id}
        name={name}
        type="file"
        accept={accept}
        required={required}
        className="sr-only"
        onChange={(event) => chooseFiles(event.target.files)}
      />
      {previewUrl ? (
        <img src={previewUrl} alt={previewAlt} className="max-h-40 w-auto rounded-md object-contain" />
      ) : (
        <div className="flex flex-col items-center gap-1 text-muted-foreground">
          <ImagePlusIcon className="size-6" aria-hidden="true" />
          <p className="text-sm">{prompt}</p>
        </div>
      )}
      <div className="flex flex-col items-center gap-1 text-sm text-muted-foreground">
        <span>{file?.name || "No file chosen"}</span>
        <Button type="button" variant="link" className="min-h-11 px-3 text-sm">
          Browse
        </Button>
      </div>
      {error && <FieldError>{error}</FieldError>}
    </div>
  );
}
